/**
 * **更新日志页（Changelog）。**
 *
 * 标题页「鸣谢」按钮上方那个入口推进来的一屏：按版本分组，每条改动一行。
 * 结构和鸣谢页同一套（`panel` + `scrollArea` + 底部返回），只有内容是数据。
 *
 * ## 怎么加一条
 *
 * 每完成一个 `feat/*` 分支，就往 `CHANGELOG` 对应版本的 `entries` 里加一行
 * `{ tag: 'feat/xxx', what: 'changelog.xxx' }`，文案写进 `src/i18n/{zh,en}/menu.ts`。
 *
 * ⚠️ `version` 是**发布分组**，不跟 `package.json` 的 `version` 走 ——
 *    一条改动归到它实际随哪个版本发布。
 *
 * ⚠️ 这个文件只 import Pixi 的 `Container` 和 `render/ui` 控件，不碰游戏核心。
 */

import { Container } from 'pixi.js';
import {
  createColumn,
  richLabel,
  pageChrome,
  panel,
  scrollArea,
  sectionTitle,
  stampButton,
  TRACKING,
  uiPad,
  INK,
} from '../../render/ui';
import { bi, richText, t, type TextKey } from '../../i18n';
import type { Screen, ScreenContext } from '../screen';

/** 和鸣谢页对齐的面板尺寸与留白 */
const PANEL_WIDTH = 760;
const PANEL_PADDING = 20;
const PANEL_TOP = 104;
const BUTTON_RESERVE = 72;
const CONTENT_INSET = 8;

/** 一条改动：`tag` 是来源（框架 / feat 分支），`what` 是 i18n 键里的一句话 */
export interface ChangeEntry {
  readonly tag: string;
  readonly what: TextKey;
}

/** 一个发布版本下的一批改动 */
export interface ChangeRelease {
  readonly version: string;
  readonly entries: readonly ChangeEntry[];
}

/**
 * 版本号是**发布分组**：一条改动归到它实际随哪个版本发布，不跟 `package.json` 的
 * `version` 走。两个 feat 分支都归在 0.1.0 底下。
 */
export const CHANGELOG: readonly ChangeRelease[] = [
  {
    version: '0.1.0',
    entries: [
      { tag: 'framework', what: 'changelog.framework' },
      { tag: 'feat/lore-rich-text', what: 'changelog.loreRichText' },
      { tag: 'feat/platform-adaptation', what: 'changelog.platformAdaptation' },
    ],
  },
];

export function createChangelogScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-changelog';
  let column = createColumn(1);

  const build = (): void => {
    const scale = ctx.settings.get().uiScale;
    const { width, height } = ctx.bounds;

    column = createColumn(scale);
    container.addChild(column.container);

    column.addRaw(pageChrome({ scale, width, height, header: bi('common.changelog'), page: 'P.07' }));

    const sheet = panel({
      scale,
      width: PANEL_WIDTH,
      title: bi('common.changelog'),
      padding: PANEL_PADDING,
    });
    sheet.container.position.set(Math.round((width - PANEL_WIDTH) / 2), uiPad(PANEL_TOP, scale));
    column.addRaw(sheet.container);

    const innerWidth = PANEL_WIDTH - uiPad(PANEL_PADDING * 2 + CONTENT_INSET, scale);
    // 从画布反推高度，而不是写死一个数再乘倍率（见 room.ts 的同名纪律）
    const overhead = uiPad(PANEL_TOP + 24 + PANEL_PADDING * 2 + BUTTON_RESERVE, scale);
    const area = scrollArea({
      scale,
      width: innerWidth,
      bleed: uiPad(CONTENT_INSET, scale),
      height: Math.max(uiPad(180, scale), height - overhead),
    });
    sheet.body.addChild(area.container);

    const inner = createColumn(scale, 10);
    area.content.addChild(inner.container);

    for (const release of CHANGELOG) {
      inner.add(sectionTitle({ text: release.version, scale, width: innerWidth }));
      for (const entry of release.entries) {
        inner.add(
          richLabel({
            text: entry.tag,
            scale,
            size: 13,
            fill: INK.text,
            role: 'mono',
            bold: true,
            tracking: TRACKING.normal,
          }),
          0,
          uiPad(8, scale),
        );
        inner.add(
          richLabel({
            text: richText(entry.what),
            scale,
            size: 11,
            fill: INK.textDim,
            role: 'cjk',
            wrapWidth: innerWidth,
          }),
          0,
          uiPad(2, scale),
        );
      }
    }

    area.setContentHeight(Math.round(inner.y));
    sheet.fitContent(area.height);

    const backButton = stampButton({
      label: t('common.back'),
      scale,
      variant: 'primary',
      onPress: () => ctx.stack.pop(),
    });
    backButton.container.position.set(
      Math.round((width - backButton.width) / 2),
      height - uiPad(BUTTON_RESERVE - 8, scale),
    );
    column.addRaw(backButton.container);
  };

  build();

  return {
    container,
    rebuild() {
      column.dispose();
      container.removeChildren();
      build();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      ctx.stack.pop();
      return true;
    },
    destroy() {
      column.dispose();
      container.destroy({ children: true });
    },
  };
}
